import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import GroupIconGlobe from "../../../../assets/images/group-icon-globe.svg";
import GroupIconCloud from "../../../../assets/images/group-icon-cloud.svg";
import TypeNetworkServicesIcon from "../../../../assets/images/type-icon-network-services.svg";
import ManagePortsService from "../../../../services/managePorts.service";
import {
  ErrorCodeMessageMapping,
  ErrorCodesMapping,
  MY_SELLER_BUCKET_PRE_URL,
  SuccessMessage,
  TERMS_DEFAULT_FILE,
  ErrorMessages as Errors,
} from "../../../../utils/apiConstants";
import SuccessInfoIcon from "../../../../assets/images/success-toast-checkmark-icon.svg";
import ErrorInfoIcon from "../../../../assets/images/error-info-icon.svg";
import { PutupForSalePayload } from "../../../../interfaces/managePorts.interface";
import SaleDrawer from ".";

export const TypeOption = [
  {
    label: "Internet",
    value: "Internet",
    icon: GroupIconGlobe,
  },
  {
    label: "Cloud",
    value: "Cloud",
    icon: GroupIconCloud,
  },
  {
    label: "Network Services",
    value: "Network Services",
    icon: TypeNetworkServicesIcon,
  },
];

export function useForSalePortHook(isMounted: boolean, refetch: () => void, loading: boolean, setLoading: (_: boolean) => void, lowerDrawer = false) {
  const [saleEdit, setSaleEdit] = useState<boolean>(false);
  const [isDBPrice, setDBPrice] = useState<{
    onDemand: boolean;
    onContract: boolean;
  }>({
    onDemand: false,
    onContract: false,
  });
  const [onContractSelected, setOnContractSelected] = useState<boolean>(false);
  const [onDemandSelected, setOnDemandSelected] = useState<boolean>(false);
  const [onContractPrice, setOnContractPrice] = useState<string>("");
  const [onDemandPrice, setOnDemandPrice] = useState<string>("");
  const [serviceDescription, setServiceDescription] = useState<string>("");
  const [selectedRowForSale, setSelectedRowForSale] = useState<any>();
  const [putForSaleOpen, setPutForSaleOpen] = useState<boolean>(false);
  const [saleTouched, setSaleTouched] = useState<boolean>(false);
  const [type, setType] = useState<string>(TypeOption[0].value);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [saleAggrementAccepted, setSaleAgreementAccepted] = useState<{
    on_contract: boolean;
    on_demand: boolean;
  }>({
    on_contract: false,
    on_demand: false,
  });
  const [aggrementTypeInfo, setAggrementTypeInfo] = useState<{
    type: string;
    url: string;
  }>({
    type: "",
    url: "",
  });
  const [removeLoadingId, setRemoveLoadingId] = useState<string>("");

  const handleTermsOpenClick = useCallback(
    (type: string) => {
      setPdfViewerOpen(true);
      const fileName = type === "on_contract" ? selectedRowForSale.terms_on_contract_url : selectedRowForSale.terms_on_demand_url;
      setAggrementTypeInfo((prev) => {
        return {
          type,
          url: fileName ? MY_SELLER_BUCKET_PRE_URL + fileName : MY_SELLER_BUCKET_PRE_URL + TERMS_DEFAULT_FILE,
        };
      });
    },
    [setPdfViewerOpen, selectedRowForSale]
  );
  const handleTypeChange = useCallback(
    (value: any) => {
      setType(value);
      setSaleTouched(true);
    },
    [setType, setSaleTouched]
  );
  const handleClosePutupForSale = useCallback(() => {
    setServiceDescription("");
    setPutForSaleOpen(false);
    setOnContractSelected(false);
    setOnDemandSelected(false);
    setOnDemandPrice("");
    setDBPrice({
      onDemand: false,
      onContract: false,
    });
    setOnContractPrice("");
    setType(TypeOption[0].value);
    setSaleEdit(false);
    setSaleTouched(false);
    setSaleAgreementAccepted({
      on_contract: false,
      on_demand: false,
    });
    setAggrementTypeInfo({
      type: "",
      url: "",
    });
  }, [
    setServiceDescription,
    setPutForSaleOpen,
    setOnContractSelected,
    setOnDemandSelected,
    setOnDemandPrice,
    setDBPrice,
    setOnContractPrice,
    setType,
    setSaleEdit,
    setSaleTouched,
    setSaleAgreementAccepted,
    setAggrementTypeInfo,
  ]);
  const handleServiceDescription = useCallback(
    (e) => {
      setServiceDescription(e.target.value);
      setSaleTouched(true);
    },
    [setServiceDescription, setSaleTouched]
  );
  const handleOnContractPriceChange = useCallback(
    (e) => {
      if (e.target.value < 0) {
        return;
      }
      const split = e.target.value.split(".");
      if (split[1]) {
        if (split[1].length <= 2) {
          setOnContractPrice(e.target.value);
        }
      } else {
        setOnContractPrice(e.target.value);
      }
      setSaleTouched(true);
    },
    [setOnContractPrice, setSaleTouched]
  );

  const handleOnDemandPriceChange = useCallback(
    (e?) => {
      if (e.target.value < 0) {
        return;
      }
      const split = e.target.value.split(".");
      if (split[1]) {
        if (split[1].length <= 2) {
          setOnDemandPrice(e.target.value);
        }
      } else {
        setOnDemandPrice(e.target.value);
      }
      setSaleTouched(true);
    },
    [setOnDemandPrice, setSaleTouched]
  );

  const handleOnContractSelected = useCallback(
    (e) => {
      let prevVal = null;
      setOnContractSelected((prev) => {
        prevVal = prev;
        return !prev;
      });
      setSaleTouched(true);
      if (prevVal) {
        setDBPrice((prev) => {
          return {
            ...prev,
            onContract: false,
          };
        });
        setSaleAgreementAccepted((prev) => {
          return {
            ...prev,
            on_contract: false,
          };
        });
      }
    },
    [setOnContractSelected, setSaleTouched, setDBPrice, setSaleAgreementAccepted]
  );

  const handlePutupForSaleClick = useCallback(() => {
    setLoading(true);
    if (saleEdit) {
      const payload: any = {
        serviceDescription,
        onContractPrice: onContractPrice && onContractSelected ? parseFloat(onContractPrice).toFixed(2) : 0,
        onDemandPrice: onDemandSelected && onDemandPrice ? parseFloat(onDemandPrice).toFixed(2) : 0,
        type,
      };

      ManagePortsService.editSaleDetails(payload, selectedRowForSale.listing_id)
        .then(() => {
          if (!isMounted) return;
          setLoading(false);
          handleClosePutupForSale();
          const message = SuccessMessage.putForSaleEditSuccess.replace("PORTNAME", selectedRowForSale.port_name);
          toast.success(message, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          refetch();
          handleClosePutupForSale();
        })
        .catch((e: any) => {
          if (!isMounted) return;
          setLoading(false);
          if (e.errorCode === ErrorCodesMapping[1010]) {
            toast.error(ErrorCodeMessageMapping[1010], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
            refetch();
            handleClosePutupForSale();
          } else {
            toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          }
        });
    } else {
      const payload: PutupForSalePayload = {
        serviceDescription,
        physicalPortIds: [selectedRowForSale?.nr_id],
        onContractPrice: onContractPrice && onContractSelected ? parseFloat(onContractPrice) : 0,
        onDemandPrice: onDemandSelected && onDemandPrice ? parseFloat(onDemandPrice) : 0,
        type,
      };

      ManagePortsService.putUpForSale(payload)
        .then(() => {
          if (!isMounted) return;
          setLoading(false);
          handleClosePutupForSale();
          const message = SuccessMessage.putForSaleSingleSuccess;
          toast.success(message, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          refetch();
        })
        .catch((e: any) => {
          if (!isMounted) return;
          setLoading(false);
          if (e.errorCode === ErrorCodesMapping[1014]) {
            toast.error(ErrorCodeMessageMapping[1014], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
            refetch();
            handleClosePutupForSale();
          } else {
            toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          }
        });
    }
  }, [
    saleEdit,
    serviceDescription,
    onDemandSelected,
    onContractSelected,
    onContractPrice,
    onDemandPrice,
    selectedRowForSale?.nr_id,
    type,
    setLoading,
    handleClosePutupForSale,
    refetch,
  ]);
  const handleOnDemandSelected = useCallback(
    (e?) => {
      let prevVal = null;
      setOnDemandSelected((prev) => {
        prevVal = prev;
        return !prev;
      });
      setSaleTouched(true);
      if (prevVal) {
        setDBPrice((prev) => {
          return {
            ...prev,
            onDemand: false,
          };
        });
        setSaleAgreementAccepted((prev) => {
          return {
            ...prev,
            on_demand: false,
          };
        });
      }
    },
    [setOnDemandSelected, setSaleTouched, setDBPrice, setSaleAgreementAccepted]
  );
  const handleTermsCloseClick = useCallback(
    (agrred?: boolean, agreementType?: string) => {
      // here
      setPdfViewerOpen(false);
      agrred &&
        agreementType &&
        setSaleAgreementAccepted((prev) => {
          return {
            ...prev,
            [agreementType]: agrred,
          };
        });
      if (agreementType) {
        if (agreementType === "on_contract") {
          handleOnContractSelected(1);
        }
        if (agreementType === "on_demand") {
          handleOnDemandSelected(1);
        }
      }
    },
    [setPdfViewerOpen, setSaleAgreementAccepted, handleOnContractSelected, handleOnDemandSelected]
  );
  const handleSaleEdit = useCallback(
    (port: any) => {
      setSelectedRowForSale(port);
      setPutForSaleOpen(true);
      setServiceDescription(port.service_description);
      setOnContractSelected(!!port.on_contract_price);
      setOnDemandSelected(!!port.on_demand_price);
      setOnDemandPrice(port.on_demand_price);
      setDBPrice({
        onContract: !!port.on_contract_price,
        onDemand: !!port.on_demand_price,
      });
      setSaleAgreementAccepted({
        on_contract: !!port.on_contract_price,
        on_demand: !!port.on_demand_price,
      });
      setOnContractPrice(port.on_contract_price);
      setType(port.type);
      setSaleEdit(true);
    },
    [
      type,
      setPutForSaleOpen,
      setSelectedRowForSale,
      setServiceDescription,
      setSaleAgreementAccepted,
      setOnContractSelected,
      setOnDemandSelected,
      setDBPrice,
      setOnDemandPrice,
      setOnContractPrice,
      setType,
      setSaleEdit,
    ]
  );
  const handlePutForSaleOpen = useCallback(
    (port: any) => {
      setSelectedRowForSale(port);
      setServiceDescription(port.group_name || port.port_description || "");
      setPutForSaleOpen(true);
    },
    [setPutForSaleOpen, setServiceDescription, setSelectedRowForSale]
  );
  const handleRemoveClick = useCallback(
    (port: any) => {
      setRemoveLoadingId(port.listing_id);

      const payload = {
        nrId: port.listing_id,
      };
      ManagePortsService.removeFromMarketPlace(payload)
        .then((response: any) => {
          if (!isMounted) return;
          toast.success(SuccessMessage.removeFromMarketPlaceSuccess, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          setRemoveLoadingId("");
          refetch();
        })
        .catch((e: any) => {
          if (!isMounted) return;
          setRemoveLoadingId("");
          if (e.errorCode === ErrorCodesMapping[1010]) {
            toast.error(ErrorCodeMessageMapping[1010], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
            refetch();
          } else {
            toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          }
        });
    },
    [setRemoveLoadingId, refetch]
  );
  const SaleDrawerComponent = (
    <SaleDrawer
      edit={saleEdit}
      isDBPrice={isDBPrice}
      saleTouched={saleTouched}
      typeOptions={TypeOption}
      type={type}
      aggrementAccepted={saleAggrementAccepted}
      onAggredTermsClick={handleTermsOpenClick}
      acceptAgreementBypass={handleTermsCloseClick}
      onTypeChange={handleTypeChange}
      loading={loading}
      selectedRowSale={selectedRowForSale}
      open={putForSaleOpen}
      onClose={handleClosePutupForSale}
      onContractSelected={onContractSelected}
      onDemandSelected={onDemandSelected}
      onContractPrice={onContractPrice}
      onDemandPrice={onDemandPrice}
      serviceDescription={serviceDescription}
      handleServiceDescription={handleServiceDescription}
      handleOnContractPriceChange={handleOnContractPriceChange}
      handleOnDemandPriceChange={handleOnDemandPriceChange}
      handlePutupForSaleClick={handlePutupForSaleClick}
      handleOnContractSelected={handleOnContractSelected}
      handleOnDemandSelected={handleOnDemandSelected}
      pdfViewerOpen={pdfViewerOpen}
      handleTermsCloseClick={handleTermsCloseClick}
      aggrementTypeInfo={aggrementTypeInfo}
      lowerDrawer={lowerDrawer}
    />
  );
  return {
    SaleDrawerComponent,
    handlePutupForSaleClick,
    handleSaleEdit,
    handlePutForSaleOpen,
    handleRemoveClick,
    removeLoadingId,
  };
}
